<template>
  <div>
    <v-container fluid id="tract-geography" v-if="locationSelection" class="pt-2 px-0 pb-0">
      <v-row justify="center" v-if="mapLoading">
        <v-col cols="auto">
          <v-progress-circular color="primary" indeterminate/>
        </v-col>
      </v-row>
      <LocationSelection
      :isEditing="isEditing"
      :spot="tract.spot"
      :pickupRadiusDimensions="tract?.pickupRange ?? 1"
      :locations="locations"
      :propTract="propTract"
      :angelGeojson="angelGeojson"
      :notesHidden="notesHidden"
      :permitNumbersHint="requirement(TractRequiredField.PermitNumbers).hint"
      @out-of-parcel-bounds="onOutOfParcelBounds"
      @geojson-import-initialized="onGeojsonImportInitialized"
      @geojson-cut-layer-removed="onGeojsonCutRemoved"
      @location-changed="locationChanged"
      @mutate-geojson-clicked="mutateGeojsonClicked"
      @acres-calculated="handleAcresCalculated"
      @permit-numbers-updated="permitNumbersChanged"
      v-else>
        <template #map-and-parcel-number>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="tract.mapAndParcelNumber"
              id="tract-parcel-number"
              :label="$t('mapAndParcelNumber')"
              color="black"
              counter
              maxlength="32"
              v-bind="requirementAttrs(TractRequiredField.MapAndParcelNumber)"
            ></v-text-field>
          </v-col>
        </template>
        <template #acres>
          <v-col cols="12" sm="4">
            <v-text-field
              id="acres"
              v-model="tract.acres"
              :label="$t('acres')"
              :rules="[rules.validAcres, rules.fourDecimalPlacesOrFewer, ...requirement(TractRequiredField.Acres).rules]"
              :hint="requirement(TractRequiredField.Acres).hint"
              :persistent-hint="requirement(TractRequiredField.Acres).hint !== ''"
              color="black"
            />
          </v-col>
        </template>
        <template #pickup-radius>
          <v-col cols="12" md="6" xl="3">
            <v-select
            color="black"
            item-color="primary"
            v-model="tract.pickupRange"
            :label="$t('pickupRadius')"
            :items="pickupRadiusValues"
              id="pickup-radius"
            />
          </v-col>
        </template>
        <template #logging-accessibility>
          <v-col cols="12" md="6" xl="5">
            <v-select
            color="black"
            item-color="primary"
            item-text="value"
            item-value="tagId"
            clearable
              id="access"
            v-model="tract.loggingAccessibilityTagId"
            :label="$t('loggingAccessibility')"
            :items="loggingAccessibilityTags"
            v-bind="requirementAttrs(TractRequiredField.LoggingAccessibilityTag)"
            />
          </v-col>
        </template>
        <template #scaling-region>
          <v-col cols="12" xl="4" v-if="showScalingRegion">
            <v-select
            :items="scalingRegions"
            :label="$t('scalingRegion')"
            v-model="tract.scalingRegion"
            item-value="value"
            item-text="name"
            :rules="scalingRegionIsRequired ? [v => !!v || $t('required')] : scalingRegionRequirement.rules"
            :hint="scalingRegionRequirement.hint"
            :persistent-hint="scalingRegionRequirement.hint !== ''"
            >
              <template #append>
                <Icon
                v-if="scalingRegionIsRequired && !tract.scalingRegion"
                color="error"
                small
                icon="mdi-alert-circle-outline"
                class="ml-4"
                :tooltipText="$t('scalingRegionRequired', { state: tract.spot.countrySubdivision })"
                />
              </template>
            </v-select>
          </v-col>
        </template>
      </LocationSelection>
    </v-container>
    <PolygonEditor
    v-else-if="polygonEditor"
    class="pt-2"
    :angelGeojson="angelGeojson"
    :title="$t('editingShapeForTract', { tract: propTract.name })"
    :tract="propTract"
    @init-parcel="getParcel"
    @shape-cancelled="onGeojsonCancelled"
    @shape-saved="onGeojsonSaved"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { ScalingRegion, TagArea, TractRequiredField } from '@/utils/Enumerations'
import { requirementAnnotation } from '@/utils/TractRequirements.js'
import { defaultCoordinates, addMapControlScript, removeMapControlScript } from '@/utils/map/MapHelpers'
import { getFormattedCoord } from '@/utils/NumericMutations'
import { angelGeojsonClient, AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { getCoordinateOnFeature, ACRES_DECIMALS } from '@/utils/map/GeospatialHelpers'
import { MAX_ACRES } from '@/utils/rules'
import { cloneDeep } from 'lodash'
import { requiredScalingRegionStates } from '@/utils/constants'

const MIN_ACRES = 0
const Mode = {
  LocationSelection: { id: 0, label: 'locationSelection' },
  PolygonEditor: { id: 1, label: 'polygonEditor' }
}
const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels

export default {
  name: 'TractGeography',

  props: {
    propTract: {
      type: Object
    },
    originalTract: {
      type: Object
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    notesHidden: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  components: {
    LocationSelection: () => import('@/components/tract/LocationSelection.vue'),
    PolygonEditor: () => import('./PolygonEditor.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    MIN_ACRES,
    MAX_ACRES,
    ACRES_DECIMALS,
    TractRequiredField,
    requiredScalingRegionStates,
    rules: fieldRules.rules,
    tract: {
      spot: null,
      acres: 0,
      mapAndParcelNumber: '',
      loggingAccessibilityTagId: undefined,
      pickupRange: 1609,
      shapefile: null,
      scalingRegion: ScalingRegion.NotDefined.value,
      permitNumbers: []
    },
    mapLoading: true,
    locations: [],
    pickupRadiusValues: [],
    loggingAccessibilityTags: [],
    mode: Mode.LocationSelection,
    angelGeojson: new AngelGeojson(),
    geojsonImportModule: null,
    storedCutLayer: null
  }),

  watch: {
    tract: {
      handler () {
        this.$emit('tract-changed', this.tract)
      },
      deep: true
    },

    'propTract.permitNumbers': {
      handler (newPermitNumbers) {
        this.tract.permitNumbers = newPermitNumbers
      },
      deep: true
    },

    showScalingRegion (shown) {
      if (!shown) this.tract.scalingRegion = ScalingRegion.NotDefined.value
    },

    mode (m) {
      this.$emit('mode-changed', m)
    }
  },

  async created () {
    this.setInitialValues(this.propTract)
    addMapControlScript()
    await this.fetchAngelGeojsonFromRemote()
    await this.setTractSpot()
    await this.getParcel()
    this.pickupRadiusValues = this.getTractPickupRadiusValues()
    this.loggingAccessibilityTags = await this.fetchTags(TagArea.LoggingAccessibility.value)
    this.mapLoading = false
  },

  beforeDestroy () {
    removeMapControlScript()
  },

  computed: {
    locationSelection () {
      return this.mode.id === Mode.LocationSelection.id
    },

    polygonEditor () {
      return this.mode.id === Mode.PolygonEditor.id
    },

    tractSpotIsUnsaved () {
      if (!this.originalTract?.spot || !this.tract?.spot) return true
      function formatCoord (coord) { return parseFloat(coord.toFixed(6)) }
      const { latitude: oldLat, longitude: oldLng } = this.originalTract.spot
      const { latitude: newLat, longitude: newLng } = this.tract.spot
      return formatCoord(oldLat) !== formatCoord(newLat) || formatCoord(oldLng) !== formatCoord(newLng)
    },

    scalingRegions () {
      return ScalingRegion.enums
    },

    scalingRegionIsRequired () {
      return this.requiredScalingRegionStates.includes(this.tract.spot?.countrySubdivision)
    },

    ...mapGetters('tract-type', ['allTractTypes']),

    selectedTractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.propTract?.type?.tractTypeId)
    },

    scalingRegionRequirement () {
      return this.requirement(TractRequiredField.ScalingRegion)
    },

    showScalingRegion () {
      return this.scalingRegionIsRequired || this.scalingRegionRequirement.hint !== ''
    }
  },

  methods: {
    requirement (fields) {
      return requirementAnnotation(this.selectedTractType, fields)
    },

    requirementAttrs (fields) {
      const { hint, rules } = this.requirement(fields)
      return { hint, 'persistent-hint': hint !== '', rules }
    },

    ...mapActions('tract', ['getLocationWithCompanyAddress', 'getLocationWithCoordinate']),
    ...mapActions('tags', ['fetchTags']),
    ...mapActions('tract', ['getParcelForCoordinate']),

    acresFloat (num) { return parseFloat(num.toFixed(ACRES_DECIMALS)) },

    async requestLocation () {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject)
      })
    },

    async getParcel () {
      if (this.angelGeojson && this.angelGeojson?.layerIsEmpty(PARCELS)) {
        const parcelForCoordinate = await this.getParcelForCoordinate({ latitude: this.tract.spot.latitude, longitude: this.tract.spot.longitude })
        if (parcelForCoordinate) {
          this.angelGeojson.setLayer(PARCELS, parcelForCoordinate.parcels)
          this.$emit('angel-geojson-mutated', this.angelGeojson)
          const parcelFeature = this.angelGeojson.getFirstFeature(PARCELS)
          if (this.noMapAndParcelNumber) this.tract.mapAndParcelNumber = parcelFeature.properties.fields.parcel_id
        }
      }
    },

    noMapAndParcelNumber () {
      return !this.tract.mapAndParcelNumber || this.tract.mapAndParcelNumber === ''
    },

    async onOutOfParcelBounds () {
      this.tract.mapAndParcelNumber = ''
      this.angelGeojson.setLayer(PARCELS, null)
      this.$emit('angel-geojson-mutated', this.angelGeojson)
      await this.getParcel()
    },

    async fetchAngelGeojsonFromRemote () {
      this.angelGeojson = new AngelGeojson(await angelGeojsonClient.readGeojson(this.propTract.tractId))
    },

    async locationChanged (spot) {
      this.tract.spot = spot
      await this.getParcel()
      this.$emit('tract-changed', this.tract)
    },

    permitNumbersChanged (permitNumbers) {
      if (permitNumbers !== null && permitNumbers !== undefined) {
        this.tract.permitNumbers = permitNumbers
      }
    },

    async setTractSpot () {
      if (!this.tract.spot.spotId) {
        try {
          const { coords: coordinate } = await this.requestLocation()
          await this.updateTractLocationToCoordinate(coordinate)
        } catch (error) {
          try {
            await this.setTractLocationToCompanyLocation()
          } catch (error) {
            this.setTractToDefaultLocation()
          }
        }
      }
    },

    async updateTractLocationToCoordinate (coordinate) {
      const { countrySubdivision, countrySecondarySubdivision } = await this.getLocationWithCoordinate(coordinate)
      this.tract.spot = {
        latitude: getFormattedCoord(coordinate.latitude),
        longitude: getFormattedCoord(coordinate.longitude),
        countrySubdivision: countrySubdivision,
        countrySecondarySubdivision: countrySecondarySubdivision
      }
    },

    async setTractLocationToCompanyLocation () {
      const spotInfo = await this.getLocationWithCompanyAddress()
      this.tract.spot = spotInfo
    },

    setTractToDefaultLocation () {
      this.tract.spot = {
        latitude: defaultCoordinates.latitude,
        longitude: defaultCoordinates.longitude,
        countrySubdivision: 'NC',
        countrySecondarySubdivision: 'Forsyth'
      }
    },

    revealMap () {
      this.mapLoading = false
    },

    setInitialValues (propTract) {
      this.tract.spot = propTract.spot ?? {
        countrySecondarySubdivision: '',
        countrySubdivision: '',
        latitude: 0,
        longitude: 0,
        name: ''
      }
      this.tract.acres = this.acresFloat(propTract.acres)
      this.tract.mapAndParcelNumber = propTract.mapAndParcelNumber
      this.tract.pickupRange = propTract.pickupRange ?? 1609
      this.tract.loggingAccessibilityTagId = propTract.loggingAccessibilityTagId
      this.tract.scalingRegion = propTract.scalingRegion
      this.tract.permitNumbers = propTract.permitNumbers
    },

    getTractPickupRadiusValues () {
      return [
        {
          text: '¼ Mile',
          value: 402
        },
        {
          text: '½ Mile',
          value: 804
        },
        {
          text: '¾ Mile',
          value: 1207
        },
        {
          text: '1 Mile',
          value: 1609
        },
        {
          text: '2 Miles',
          value: 3218
        },
        {
          text: '3 Miles',
          value: 4828
        }
      ]
    },

    mutateGeojsonClicked () {
      this.mode = Mode.PolygonEditor
    },

    async onGeojsonCancelled () {
      this.mode = Mode.LocationSelection
      if (this.geojsonImportModule) {
        this.angelGeojson.setLayer(CUT, null)
        this.geojsonImportModule = null
      }
    },

    async onGeojsonSaved ({ cut, acres }) {
      this.handleAcresCalculated(acres)
      this.handleCutSaved(cut)
      if (this.geojsonImportModule) {
        this.geojsonImportModule = null
      }
      this.mode = Mode.LocationSelection
    },

    handleCutSaved (cut) {
      this.angelGeojson.setLayer(CUT, cut)
      this.$emit('angel-geojson-mutated', this.angelGeojson)
      this.$emit('cut-layer-changed')
    },

    handleAcresCalculated (acres) {
      this.tract.acres = acres
    },

    async onGeojsonImportInitialized (geojsonImportModule) {
      this.geojsonImportModule = geojsonImportModule
      await this.updateLocationForGeojsonImport(geojsonImportModule)
      this.angelGeojson.setLayer(CUT, geojsonImportModule.angelGeojson.getLayer(CUT))
      this.mode = Mode.PolygonEditor
    },

    async updateLocationForGeojsonImport (geojsonImportModule) {
      const { angelGeojson } = geojsonImportModule
      const coord = getCoordinateOnFeature(angelGeojson.getFirstFeature(CUT))
      await this.updateTractLocationToCoordinate(coord)
    },

    async onGeojsonCutRemoved () {
      const angelGeojson = cloneDeep(this.angelGeojson)
      angelGeojson.setLayer(CUT, null)
      this.angelGeojson = angelGeojson
      this.$emit('angel-geojson-mutated', angelGeojson)
      this.$emit('cut-layer-changed')
      this.propTract.spot
        ? this.tract.spot = cloneDeep(this.propTract.spot)
        : await this.setTractSpot()
      this.mode = Mode.LocationSelection
    }
  }
}
</script>
